import { Booking, CommodityPids, PidClass } from "../../../features/bookingSlice";
import { getIncompleteIndexes, getPidAcknowledgement } from "../../../utilities/PidUtilities";
import { isPidSupplied, normalisePid } from "../../../utilities/ValidationUtilities";

// Builders for the PID parts of the booking request (specs/014 contracts/booking-request-pid.md).

export interface ProductIdentifier {
  type: 'MERCHANT' | 'MANUFACTURER' | 'STANDARDISED',
  value: string
}

const PID_TYPES: { field: keyof CommodityPids, type: ProductIdentifier['type'] }[] = [
  { field: 'merchant', type: 'MERCHANT' },
  { field: 'manufacturer', type: 'MANUFACTURER' },
  { field: 'standardised', type: 'STANDARDISED' }
];

export const isPidLane = (pidClass: PidClass) => pidClass === 'B2B' || pidClass === 'B2C';

export function buildProductIdentifiers(pids?: CommodityPids): ProductIdentifier[] | undefined {
  if (!pids) return undefined;
  const identifiers = PID_TYPES
    .filter(({ field }) => isPidSupplied(pids[field]))
    .map(({ field, type }) => ({ type, value: normalisePid(pids[field]) }));
  return identifiers.length > 0 ? identifiers : undefined;
}

export function addProductIdentifiers<T>(
  commodityRequests: T[] | null,
  bookingCommodities: { productIdentifiers?: CommodityPids }[],
  pidClass: PidClass
): T[] | null {
  if (!commodityRequests || !isPidLane(pidClass)) return commodityRequests;
  return commodityRequests.map((request, index) => {
    const productIdentifiers = buildProductIdentifiers(bookingCommodities[index]?.productIdentifiers);
    return productIdentifiers ? { ...request, productIdentifiers } : request;
  });
}

export function buildPidAcknowledgement(booking: Booking, pidClass: PidClass) {
  const acknowledgement = getPidAcknowledgement(booking);
  if (
    pidClass !== 'B2C'
    || !acknowledgement.acknowledged
    || getIncompleteIndexes(booking.customsDetails.commodities).length === 0
  ) {
    return undefined;
  }
  return {
    acknowledged: true,
    acknowledgedAt: acknowledgement.acknowledgedAt
  };
}

const PID_ERROR_MESSAGES: { [code: string]: string } = {
  PID_INVALID: 'A product identifier must be up to 35 characters using only letters, numbers and - _ . /',
  PID_REQUIRED: 'Merchant and manufacturer product identifiers are required for this shipment.'
};

// Shows PID errors against the commodity the customer sees, instead of the raw field path.
export function formatPidError<T extends { code: string, message: string, fieldName: string }>(error: T): T {
  const friendlyMessage = PID_ERROR_MESSAGES[error?.code];
  if (!friendlyMessage) return error;
  const index = /commodities\[(\d+)\]/.exec(error.fieldName ?? '');
  return {
    ...error,
    fieldName: index ? `Commodity ${+index[1] + 1}` : 'Commodities',
    message: friendlyMessage
  };
}

export function hasPidIncompleteAlert(bookingResult?: { alerts?: { code: string }[] }) {
  return !!bookingResult?.alerts?.some(alert => alert.code === 'PID_INCOMPLETE');
}
